import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  getDoc,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';
import { getCurrentPeriod } from '../utils/periodHelpers';

export function useCheckoffs() {
  const { user } = useAuth();
  const [checkoffs, setCheckoffs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setCheckoffs([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'checkoffs'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const checkoffsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setCheckoffs(checkoffsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching checkoffs:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const isChecked = useCallback(
    (linkId, frequency) => {
      const currentPeriod = getCurrentPeriod(frequency);
      return checkoffs.some(
        (c) => c.linkId === linkId && c.period === currentPeriod
      );
    },
    [checkoffs]
  );

  const toggleCheck = async (linkId, frequency) => {
    if (!user) return;

    const currentPeriod = getCurrentPeriod(frequency);

    // Use deterministic ID to prevent duplicates and enable atomic toggle
    const checkoffId = `${user.uid}_${linkId}_${currentPeriod}`;
    const checkoffRef = doc(db, 'checkoffs', checkoffId);

    try {
      // Use transaction for atomic check-and-toggle
      await runTransaction(db, async (transaction) => {
        const checkoffDoc = await transaction.get(checkoffRef);

        if (checkoffDoc.exists()) {
          // Uncheck - delete the checkoff
          transaction.delete(checkoffRef);
        } else {
          // Check - create new checkoff
          transaction.set(checkoffRef, {
            linkId,
            userId: user.uid,
            period: currentPeriod,
            checkedAt: serverTimestamp()
          });
        }
      });
    } catch (err) {
      console.error('Error toggling checkoff:', err);
      throw err;
    }
  };

  // Clean up old checkoffs (called periodically or on login)
  const cleanupOldCheckoffs = async () => {
    if (!user) return;

    const currentDaily = getCurrentPeriod('daily');
    const currentWeekly = getCurrentPeriod('weekly');
    const currentMonthly = getCurrentPeriod('monthly');

    try {
      const q = query(
        collection(db, 'checkoffs'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);

      const deletePromises = [];
      snapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        // We can't easily determine frequency from the checkoff alone
        // So we keep all current period checkoffs
        // This is a simplified cleanup - in production you might want to store frequency
        const period = data.period;
        const isCurrentDaily = period === currentDaily;
        const isCurrentWeekly = period === currentWeekly;
        const isCurrentMonthly = period === currentMonthly;

        if (!isCurrentDaily && !isCurrentWeekly && !isCurrentMonthly) {
          deletePromises.push(deleteDoc(doc(db, 'checkoffs', docSnapshot.id)));
        }
      });

      await Promise.all(deletePromises);
    } catch (err) {
      console.error('Error cleaning up checkoffs:', err);
    }
  };

  return {
    checkoffs,
    loading,
    isChecked,
    toggleCheck,
    cleanupOldCheckoffs
  };
}
